import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError, type PostValue } from '../../api/client'
import {
  isProtecteeSettingsTab,
  post,
  profileDevicesQuery,
  profileQuery,
  profilesTableQuery,
  protecteeSettingsShellQuery,
  type ProfileSheet,
  type ProtecteeSettingsTab,
} from '../../api/settingsProtectee'
import { AlgorithmForm, AlgorithmView, Tree, TreeForm, collect, formFields } from '../../protectee/backupForms'
import { escapeHtml } from '../../inventoree/format'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Modal } from '../../ui/Modal'
import { SelectPicker } from '../../ui/SelectPicker'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { RestoreTestsPanel } from './RestoreTestsPanel'

/**
 * Settings → Protectee: `protectee_settings/tabList.php` + Backup profiles
 * (`backup_profiles/index.php`, `actionTabs.php` cu filele Settings / Devices,
 * `editProfileDetails.php` cand profilul nu e purtat de nicio masina,
 * `profileDetails.php` altfel, `profileDevices.php` + `assignProfileModal.php`),
 * element cu element.
 */
export function ProtecteeSettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(protecteeSettingsShellQuery())

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isProtecteeSettingsTab(tab)) return
    const first = shell.data.tabs[0]
    if (first) void navigate({ to: '/settings/protectee/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!shell.data) return <Spinner />
  const current = isProtecteeSettingsTab(tab) ? tab : (shell.data.tabs[0]?.id ?? '')

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          <li className="menu-header">Protectee</li>
          {shell.data.tabs.map((item) => (
            <SidebarTab key={item.id} tab={item} current={current} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        {current === 'backup_profiles' && <ProfilesPanel />}
        {current === 'restore_tests' && <RestoreTestsPanel />}
      </div>
    </>
  )
}

function SidebarTab({ tab, current }: { tab: ProtecteeSettingsTab; current: string }) {
  const navigate = useNavigate()
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    void navigate({ to: '/settings/protectee/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={tab.id === current ? 'menu-tab active' : 'menu-tab'}>
      <a href={'/app/settings/protectee/' + tab.id} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

async function send(path: string, fields: Record<string, PostValue>, onDone?: () => void): Promise<boolean> {
  try {
    const body = await post(path, fields)
    toast(body.message ?? 'Saved.', 'Success', 'success')
    onDone?.()
    return true
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    return false
  }
}

/* --------------------------------------------------------------- lista */

export const PROFILES_TABLE = 'backup-profiles'

const DASH = '<i class="fas fa-minus font-10"></i>'

/** `profilesModel` din public/js/settings/protectee.js, coloana cu coloana. */
function profileColumns(): TableColumn[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(r.ID)}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Name', orderable: true, visible: true, html: (r) => (r.name ? escapeHtml(r.name) : DASH) },
    { index: 2, title: 'Destination', orderable: true, visible: true, html: (r) => (r.destination ? `<span class="badge badge-primary">${escapeHtml(r.destination)}</span>` : DASH) },
    { index: 3, title: 'Path', orderable: true, visible: true, html: (r) => escapeHtml(r.path ?? '') },
    { index: 4, title: 'Created', orderable: true, visible: true, html: (r) => escapeHtml(r.created ?? '') },
    {
      index: 5,
      title: 'Actions',
      orderable: false,
      visible: true,
      /* `Backup_profiles::removeProfileForm`: un profil purtat de masini nu se sterge - butonul sta stins. */
      html: (r) =>
        `<button type="button" class="btn btn-sm btn-danger deleteProfile" data-id="${Number(r.ID)}"${Number(r.devices) > 0 ? ' disabled title="The profile is assigned to devices"' : ''}><i class="fas fa-trash"></i> Delete</button>`,
    },
  ]
}

/** `backup_profiles/index.php`: `#backup-profiles`, serverSide, cu randul desfasurat Settings / Devices. */
function ProfilesPanel() {
  const client = useQueryClient()
  const [state, setState] = useTableState(PROFILES_TABLE, { start: 0, length: 25, order: { column: 1, dir: 'asc' }, search: '' })
  const columns = useMemo(() => profileColumns(), [])
  const table = useQuery({
    ...profilesTableQuery({ start: state.start, length: state.length, orderColumn: state.order.column, orderDir: state.order.dir, search: state.search }),
    placeholderData: keepPreviousData,
  })
  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'protectee', 'profiles'] })

  if (table.error instanceof ApiError && table.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    const button = (e.target as HTMLElement).closest<HTMLButtonElement>('button.deleteProfile')
    if (!button || button.disabled) return
    if (!confirm('Are you sure you want to delete this profile?')) return
    void send(`/profiles/${Number(button.dataset.id)}/delete`, {}, () => void reload())
  }

  return (
    <div id="content" onClick={onClick}>
      <header className="panel-heading" style={{ borderBottom: 0 }}>
        <h3>
          <strong>Backup</strong> profiles
        </h3>
        <label className="color">backup profiles settings</label>
      </header>
      <DataTable
        id={PROFILES_TABLE}
        columns={columns}
        rows={table.data?.data ?? []}
        total={table.data?.recordsFiltered ?? table.data?.recordsTotal ?? 0}
        fetching={table.isFetching}
        error={table.isError ? (table.error as Error).message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(r) => String(r.DT_RowId)}
        emptyHint="A backup profile is created from a device task: Protectee > All Devices > Backup settings > Save as profile."
        details={(row) => <ProfileDetails id={Number(row.ID)} onChanged={() => void reload()} />}
      />
    </div>
  )
}

/** `backup_profiles/others/actionTabs.php`: Settings si Devices. */
function ProfileDetails({ id, onChanged }: { id: number; onChanged: () => void }) {
  const [tab, setTab] = useState<'settings' | 'devices'>('settings')
  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          <li>
            <a href="/app/settings/protectee/backup_profiles" className={'action_button profile-settings' + (tab === 'settings' ? ' active' : '')} onClick={(e) => { e.preventDefault(); setTab('settings') }}>
              Settings
            </a>
          </li>
          <li>
            <a href="/app/settings/protectee/backup_profiles" className={'action_button profile-devices' + (tab === 'devices' ? ' active' : '')} onClick={(e) => { e.preventDefault(); setTab('devices') }}>
              Devices
            </a>
          </li>
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === 'settings' ? <ProfileSettings id={id} onChanged={onChanged} /> : <ProfileDevices id={id} onChanged={onChanged} />}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- setari */

/** `editProfileDetails.php` (editabil) / `profileDetails.php` (numai citire, cand e purtat de masini). */
function ProfileSettings({ id, onChanged }: { id: number; onChanged: () => void }) {
  const client = useQueryClient()
  const q = useQuery(profileQuery(id))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const d = q.data
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['settings', 'protectee', 'profiles', id] })
    onChanged()
  }
  if (!d.editable) return <ProfileReadOnly sheet={d} />
  return (
    <>
      <div className="col-md-6">
        <div className="panel panel-primary">
          <div className="panel-heading">Settings</div>
          <div className="panel-body">
            <ProfileSettingsForm sheet={d} onSubmit={(fields) => void send(`/profiles/${id}/settings`, fields, refresh)} />
          </div>
        </div>
        <div className="panel panel-primary">
          <div className="panel-heading">Directory Tree</div>
          <div className="panel-body">
            <TreeForm tree={d.tree} submitLabel="Update" onSubmit={(paths) => void send(`/profiles/${id}/tree`, { directory: paths }, refresh)} />
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className="panel panel-primary">
          <div className="panel-heading">Algorithm</div>
          <div className="panel-body">
            <AlgorithmForm backup={d} algorithm={d.algorithm} submitLabel="Update" onSubmit={(fields) => void send(`/profiles/${id}/algorithm`, fields, refresh)} />
          </div>
        </div>
      </div>
    </>
  )
}

function ProfileSettingsForm({ sheet, onSubmit }: { sheet: ProfileSheet; onSubmit(fields: Record<string, PostValue>): void }) {
  const p = sheet.profile
  const [destination, setDestination] = useState(String(p.destination_id || ''))
  const azure = destination === '3'
  return (
    <form className="form-horizontal labelcustomize tk-action-form changeProfileSettings" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); onSubmit(formFields(e.currentTarget)) }}>
      <input type="hidden" name="company" value={p.company_id} />
      <div className="form-group">
        <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}><span className="color">Name</span></label>
        <div className="col-sm-6"><input type="text" className="form-control" name="profileName" defaultValue={p.name} required /></div>
      </div>
      <div className="form-group">
        <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}><span className="color">Destination</span></label>
        <div className="col-sm-6 selectContainer">
          <SelectPicker id={`profile-destination-${p.ID}`} name="destination" title="Select destination" value={destination} onChange={setDestination} options={sheet.destinations.map((d) => ({ value: String(d.ID), label: d.name }))} />
        </div>
      </div>
      {azure ? (
        <>
          <div className="form-group azure-key">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}><span className="color">Azure key</span></label>
            <div className="col-sm-6"><input type="text" className="form-control" name="azureKey" defaultValue={p.destination_key} required /></div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}><span className="color">Azure account</span></label>
            <div className="col-sm-6"><input type="text" className="form-control" name="user" defaultValue={p.user} /></div>
          </div>
        </>
      ) : (
        <>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}><span className="color">Path</span></label>
            <div className="col-sm-6"><input type="text" className="form-control" name="path" defaultValue={p.path} required /></div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}><span className="color">User</span></label>
            <div className="col-sm-6"><input type="text" className="form-control" name="user" defaultValue={p.user} /></div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}><span className="color">Password</span></label>
            <div className="col-sm-6"><input type="password" className="form-control" name="password" defaultValue={p.has_password ? '********' : ''} /></div>
          </div>
        </>
      )}
      <button type="submit" className="btn btn-sm btn-primary updateSettings">Update</button>
    </form>
  )
}

/** `profileDetails.php`: profilul purtat de masini se citeste, nu se schimba. */
function ProfileReadOnly({ sheet }: { sheet: ProfileSheet }) {
  const p = sheet.profile
  const dest = sheet.destinations.find((x) => x.ID === p.destination_id)
  return (
    <div className="col-md-12">
      <div className="alert alert-info">
        <i className="fas fa-info-circle"></i> Profile is assigned to devices. In order to change it, you must remove it from devices first.
      </div>
      <table className="table table-settings">
        <tbody>
          <tr><td className="title">Name</td><td>{p.name}</td></tr>
          <tr><td className="title">Destination</td><td>{dest?.name ?? p.destination_id}</td></tr>
          {p.destination_id === 3 ? (
            <>
              <tr><td className="title">Azure key</td><td>{p.destination_key}</td></tr>
              <tr><td className="title">Azure account</td><td>{p.user}</td></tr>
            </>
          ) : (
            <>
              <tr><td className="title">Path</td><td>{p.path}</td></tr>
              <tr><td className="title">User</td><td>{p.user}</td></tr>
            </>
          )}
        </tbody>
      </table>
      <div className="row">
        <div className="col-md-6">
          <div className="panel panel-primary">
            <div className="panel-heading">Directory Tree</div>
            <div className="panel-body">
              <Tree nodes={sheet.tree} checked={collect(sheet.tree, new Set())} onToggle={() => undefined} disabled />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="panel panel-primary">
            <div className="panel-heading">Algorithm</div>
            <div className="panel-body">
              <AlgorithmView algorithm={sheet.algorithm} lists={sheet} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- masini */

/** `profileDevices.php` + `assignProfileModal.php`. */
function ProfileDevices({ id, onChanged }: { id: number; onChanged: () => void }) {
  const client = useQueryClient()
  const q = useQuery(profileDevicesQuery(id))
  const [open, setOpen] = useState(false)
  const [chosen, setChosen] = useState<string[]>([])
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['settings', 'protectee', 'profiles', id] })
    onChanged()
  }
  const assign = () => {
    if (chosen.length === 0) return
    void send(`/profiles/${id}/assign`, { devices: chosen }, () => { setOpen(false); setChosen([]); refresh() })
  }
  return (
    <>
      <div className="col-md-12">
        <button className="btn btn-success btn-sm pull-right task-form" style={{ marginLeft: 10 }} onClick={() => setOpen(true)}>
          <i className="fas fa-plus-circle"></i> Assign profile
        </button>
        <Modal
          id={`assignProfileModal-${id}`}
          open={open}
          onClose={() => setOpen(false)}
          title="Assign Profile"
          footer={
            <>
              <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>Close</button>
              <button type="button" className="btn btn-primary saveDevices" onClick={assign}>Save</button>
            </>
          }
        >
          {q.data.allDevices.length > 0 ? (
            <form className="form-horizontal labelcustomize tk-action-form assignProfile" onSubmit={(e) => { e.preventDefault(); assign() }}>
              <input type="hidden" name="profile" value={id} />
              <div className="form-group">
                <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}><span className="color">Devices</span></label>
                <div className="col-sm-6 selectContainer">
                  <SelectPicker id={`assign-devices-${id}`} name="devices[]" title="Select devices" multiple value={chosen} onChange={setChosen} options={q.data.allDevices.map((d) => ({ value: String(d.ID), label: d.Denumire }))} />
                </div>
              </div>
            </form>
          ) : (
            <div className="alert alert-danger"><strong>No devices</strong></div>
          )}
        </Modal>
      </div>
      <div className="col-md-12">
        {q.data.devices.length > 0 ? (
          q.data.devices.map((d) => (
            <div className="col-md-3 device-box" key={d.device_id}>
              <div className="licensed-device bg-darkblue color-white">
                <div className="name"><i className="fas fa-desktop"></i> {d.device_name}</div>
                <div className="user"><i className="fa fa-user"></i> {d.logged_user}</div>
                <form className="actions remove_device_profile" onSubmit={(e) => { e.preventDefault(); void send(`/profiles/${id}/devices/remove`, { device: d.device_id }, refresh) }}>
                  <input type="hidden" name="profile" value={d.profile_id} />
                  <input type="hidden" name="device" value={d.device_id} />
                  <button type="submit" className="btn btn-xs bg-transparent"><i className="fas fa-times"></i></button>
                </form>
              </div>
            </div>
          ))
        ) : (
          <div className="col-md-12">
            <div className="alert alert-info"><i className="fas fa-info-circle"></i> No devices for this profile.</div>
          </div>
        )}
      </div>
    </>
  )
}
